import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PartnerStoreService } from '@rms/partner';
import { AppError } from '@rms/sdk/errors';
import { isUserRole, User, UserStoreService } from '@rms/user';
import { catchError, Observable, of, switchMap, tap, throwError } from 'rxjs';

import { InvalidOtpError, PartnerDashboardAccessDeniedError } from '../errors';
import { AccessTokenClaims, AUTH_ERROR_CODE, LoginResponse } from '../models';
import { AuthStoreService } from '../store';
import { decodeAccessToken, isAccessTokenExpired } from '../utils';
import { AuthApiService } from './auth-api.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly api = inject(AuthApiService);
  private readonly store = inject(AuthStoreService);
  private readonly userStore = inject(UserStoreService);
  private readonly partnerStore = inject(PartnerStoreService);

  public login(username: string, password: string): Observable<void> {
    return this.api.login(username, password).pipe(
      catchError((error: HttpErrorResponse) =>
        throwError(() =>
          AppError.from(
            error,
            error.status === HttpStatusCode.Unauthorized ? 'auth.errors.invalid-credentials' : 'auth.errors.login'
          )
        )
      ),
      switchMap((response: LoginResponse) => {
        if (!this.hasAccess(decodeAccessToken(response.access_token))) {
          return throwError(() => new PartnerDashboardAccessDeniedError());
        }

        this.store.setToken(response.access_token);

        return of(undefined);
      })
    );
  }

  public logout(): Observable<void> {
    return this.api.logout().pipe(
      catchError(() => of(undefined)),
      tap(() => this.clearSession())
    );
  }

  public requestOneTimePassword(email: string): Observable<void> {
    return this.api
      .requestOneTimePassword(email)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          throwError(() =>
            AppError.from(
              error,
              this.isOtpInvalid(error)
                ? 'auth.errors.request-one-time-password.attempts'
                : 'auth.errors.request-one-time-password.general'
            )
          )
        )
      );
  }

  public resetPassword(email: string, password: string, otp: string): Observable<void> {
    return this.api
      .resetPassword(email, password, otp)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          throwError(() =>
            this.isOtpInvalid(error) ? new InvalidOtpError(error) : AppError.from(error, 'auth.errors.reset-password')
          )
        )
      );
  }

  public clearSession(): void {
    this.store.resetStore();
    this.userStore.resetUser();
    this.partnerStore.resetStore();
  }

  public isAuthenticated(): boolean {
    const claims = this.store.getClaims();

    return !!claims && !isAccessTokenExpired(claims) && this.hasAccess(claims);
  }

  public getCurrentUser(): User | null {
    const claims = this.store.getClaims();

    if (!claims) {
      return null;
    }

    return {
      id: claims.user_id,
      email: claims.username,
      full_name: claims.user_full_name,
      roles: claims.roles.filter(isUserRole),
    };
  }

  private isOtpInvalid(error: HttpErrorResponse): boolean {
    const body: unknown = error.error;

    if (typeof body !== 'object' || body === null || !('errors' in body) || !Array.isArray(body.errors)) {
      return false;
    }

    const [first]: unknown[] = body.errors;

    return typeof first === 'object' && first !== null && 'code' in first && first.code === AUTH_ERROR_CODE.OTP_INVALID;
  }

  private hasAccess(claims: AccessTokenClaims | null): boolean {
    return !!claims && claims.roles.some(isUserRole);
  }
}
